import { DictionaryRecordContextProvider } from "@/components/dictionaries/DictionaryContext";
import DictionaryLoader from "@/components/dictionaries/DictionaryLoader";
import DictionaryPageHeader from "@/components/dictionaries/DictionaryPageHeader";
import { useToast } from "@/components/ui/use-toast";
import { Module } from "@/constants";
import { DictionaryContext } from "@/containers/dictionaries/types";
import { useFormResetWithQueryData } from "@/hooks/useFormResetWithQueryData";
import { useFormUnsavedChangesWarning } from "@/hooks/useFormUnsavedChangesWarning";
import { useQueryCacheUpdater } from "@/hooks/useQueryCacheUpdater";
import ProjectTypeForm from "@/modules/projectTypes/components/ProjectTypeForm";
import { useProjectTypesDictionary } from "@/modules/projectTypes/hooks/useProjectTypesDictionary";
import { updateProjectType } from "@/schemas/projectType";
import { ApiError } from "@/services/api";
import { ReadProjectType, UpdateProjectType } from "@/types/projectType";
import { zodResolver } from "@hookform/resolvers/zod";
import { MutationFunction, useMutation, useQuery } from "@tanstack/react-query";
import { exists } from "i18next";
import { DefaultValues, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

export default function ProjectTypeEditor({ id }: DictionaryContext) {
  const { t } = useTranslation();
  const config = useProjectTypesDictionary();
  const navigate = useNavigate();
  const { toast } = useToast();
  const labels = config.getLabels(t);
  const { updateQueryDataForExistingItem } = useQueryCacheUpdater(
    Module.ProjectTypes,
  );

  const query = useQuery({
    queryKey: [config.ns, config.id, id],
    queryFn: async () => config.service.get(id),
  });

  const mutationFn: MutationFunction<
    ReadProjectType,
    UpdateProjectType
  > = async (values) => config.service.patch(id, values);

  const mutation = useMutation<ReadProjectType, ApiError, UpdateProjectType>({
    mutationFn,
    onSuccess: (response) => {
      const id = response.id;
      navigate(`${config.path}/${id}`);
      toast({
        title: labels.success.update,
      });
      updateQueryDataForExistingItem(response);
    },
    onError: ({ errorCode }) => {
      const description = exists(`errors.${errorCode}`)
        ? t(`errors.${errorCode}`)
        : t("errors.unknownError");
      toast({
        title: labels.errors.update,
        description,
        variant: "destructive",
      });
    },
  });

  const form = useForm<UpdateProjectType>({
    resolver: zodResolver(updateProjectType),
    defaultValues: {} as DefaultValues<UpdateProjectType>,
    disabled: mutation.isPending,
  });

  useFormUnsavedChangesWarning(form);
  useFormResetWithQueryData(form, query);

  return (
    <DictionaryRecordContextProvider
      value={{
        query,
        config,
        mutation,
        form,
        id,
      }}
    >
      <DictionaryPageHeader />
      {query.isLoading && <DictionaryLoader />}
      {!query.isLoading && <ProjectTypeForm includeProjectDependsOn={false} />}
    </DictionaryRecordContextProvider>
  );
}
